import { useState } from 'react';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { DialogSurface, EmptyState, ListRow, SearchField } from '../../design-system';
import type { AbsenceRequestableTimeOffType } from './model';

const amount = (value: string) => `${Number(value).toLocaleString('ko-KR', { maximumFractionDigits: 4 })}일`;
const date = (value: string) => value.replaceAll('-', '.');

function LeaveSummary({ type, detailed }: { type: AbsenceRequestableTimeOffType; detailed: boolean }) {
  if (type.usageLimitStatus === 'CONFLICT') return <>적용 정책 중복 · 관리자 확인 필요</>;
  if (type.usageLimitStatus === 'FUTURE' && type.availableFrom) return <>{date(type.availableFrom)}부터 사용 가능</>;
  const limit = type.usageLimit;
  if (!limit) return <>날짜 선택 후 한도 확인</>;
  const period = limit.limitPeriod === 'MONTHLY' ? `${Number(limit.periodStart.slice(5, 7))}월` : `${limit.periodStart.slice(0, 4)}년`;
  return <Stack spacing={0.5}>
    <Typography variant={detailed ? 'body2' : 'caption'} color="text.secondary"><Box component="span" sx={{ whiteSpace: 'nowrap' }}>{period} 한도 {amount(limit.limitQuantity)}</Box> · <Box component="span" sx={{ color: 'text.primary', fontWeight: 600, whiteSpace: 'nowrap' }}>신청 가능 {amount(limit.availableQuantity)}</Box></Typography>
    {detailed && <>
      <Typography variant="body2" color="text.secondary">사용 {amount(limit.usedQuantity)} · 승인 대기 {amount(limit.pendingQuantity)}</Typography>
      <Typography variant="caption" color="text.secondary">{date(limit.periodStart)} – {date(limit.periodEnd)}</Typography>
    </>}
  </Stack>;
}

function LeaveRows({ types, canRequest, detailed = false, onRequest }: {
  types: AbsenceRequestableTimeOffType[]; canRequest: boolean; detailed?: boolean; onRequest: (id: string) => void;
}) {
  return <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
    {types.map((type, index) => <Box component="li" key={type.id}>
      {index > 0 && <Divider />}
      <ListRow label={type.descriptor} description={<LeaveSummary type={type} detailed={detailed} />}
        density="compact" ariaLabel={canRequest ? `${type.descriptor} 신청` : type.descriptor}
        onClick={canRequest ? () => onRequest(type.id) : undefined} />
    </Box>)}
  </Box>;
}

export function SelfRequestableLeaveList({ types, canRequest, onRequest }: {
  types: AbsenceRequestableTimeOffType[]; canRequest: boolean; onRequest: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const unique = [...new Map(types.map((type) => [type.id, type])).values()];
  const normalizedQuery = query.normalize('NFKC').trim().toLocaleLowerCase('ko-KR');
  const filtered = unique.filter((type) => type.descriptor.normalize('NFKC').toLocaleLowerCase('ko-KR').includes(normalizedQuery));
  const close = () => { setOpen(false); setQuery(''); };
  const request = (id: string) => {
    if (!canRequest) return;
    close();
    onRequest(id);
  };
  if (!unique.length) return null;
  return <>
    <Button variant="outlined" fullWidth aria-haspopup="dialog" onClick={() => setOpen(true)}>신청 가능한 휴가</Button>
    <DialogSurface open={open} onClose={close} title="신청 가능한 휴가" closeLabel="신청 가능한 휴가 닫기" size="standard" mobilePresentation="fullscreen"
      headerContent={<SearchField label="휴가 검색" placeholder="휴가 이름 검색" value={query} onValueChange={setQuery} />}>
      <Typography variant="caption" color="text.secondary" role="status">{normalizedQuery ? `검색 결과 ${filtered.length}개` : `전체 ${unique.length}개`}</Typography>
      {filtered.length ? <LeaveRows types={filtered} canRequest={canRequest} detailed onRequest={request} />
        : <EmptyState>검색 결과가 없습니다.</EmptyState>}
    </DialogSurface>
  </>;
}
